import React from 'react';
import styled from 'styled-components';
import type { ActivityKind } from '../pages/CrmSalesTracking/types';

export interface ActivityItem {
  id: string;
  initials: string;
  user: string;
  kind: ActivityKind;
  action: string;
  target: string;
  targetType?: 'action_list' | 'pos';
  targetId?: number;
  detail?: string;
  when: string;
  privacy?: 'shared' | 'private';
  with_?: string;
  posCount?: number;
  sentTo?: string;
  recipientCount?: number;
}

interface ActivityFeedProps {
  items: ActivityItem[];
  onItemClick?: (item: ActivityItem) => void;
}

const AVATAR_COLORS = [
  '#C84B1F',
  '#4A6B3A',
  '#2B3D54',
  '#8A5A3C',
  '#6B4E7D',
  '#B68A2E'
];

function userColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++)
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function verbForKind(kind: ActivityKind, action: string): string {
  switch (kind) {
    case 'create':
      return 'created action list';
    case 'share':
      return 'shared';
    case 'note':
      return action || 'added a private note on';
    case 'edit':
      return action || 'edited';
    case 'visit':
      return action || 'logged a tasting visit at';
    case 'status':
      return action || 'changed status of';
    case 'email':
      return action || 'emailed';
    default:
      return action;
  }
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  items,
  onItemClick
}) => (
  <FeedWrapper>
    {items.length === 0 && (
      <EmptyState>No activity recorded yet for this account.</EmptyState>
    )}
    {items.map((item) => (
      <Row
        key={item.id}
        onClick={() => onItemClick?.(item)}
        role="button"
        tabIndex={0}
      >
        <Avatar $color={userColor(item.user)}>{item.initials}</Avatar>
        <Content>
          <ActivityLine>
            <Actor>{item.user}</Actor>{' '}
            <Verb>{verbForKind(item.kind, item.action)}</Verb>{' '}
            <Target>{item.target}</Target>
          </ActivityLine>
          <Meta>
            {item.kind === 'create' && item.privacy && (
              <Pill $variant={item.privacy}>
                {item.privacy === 'shared' ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                )}
                {item.privacy === 'shared' ? 'Shared' : 'Private'}
              </Pill>
            )}
            {item.kind === 'create' &&
              item.privacy === 'shared' &&
              item.sentTo && (
                <>
                  <Dot />
                  <span>
                    with <Recipient>{item.sentTo}</Recipient>
                  </span>
                </>
              )}
            {item.kind === 'create' && item.posCount && (
              <>
                <Dot />
                <span>{item.posCount} POS</span>
              </>
            )}
            {item.kind === 'share' && item.with_ && (
              <Pill $variant="shared">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                {item.with_}
              </Pill>
            )}
            {item.kind === 'note' && (
              <Pill $variant="private">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Private
              </Pill>
            )}
            {item.kind === 'status' && item.detail && (
              <span>{item.detail}</span>
            )}
            {item.kind === 'email' && (
              <Pill $variant="email">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                  <polyline points="22 6 12 13 2 6" />
                </svg>
                Email sent
              </Pill>
            )}
          </Meta>
        </Content>
        <When>{item.when}</When>
      </Row>
    ))}
  </FeedWrapper>
);

const FeedWrapper = styled.div`
  padding: 12px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 26px;
  border-bottom: 1px solid var(--line-soft, var(--line));
  transition: background 0.12s;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--paper);
  }

  &:hover .activity-target {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--terracotta);
    outline-offset: -2px;
  }
`;

const Avatar = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  display: grid;
  place-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--white, #fff);
  flex-shrink: 0;
  letter-spacing: 0.02em;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityLine = styled.div`
  font-size: 13.5px;
  color: var(--ink);
  line-height: 1.5;
  margin-bottom: 4px;
`;

const Actor = styled.span`
  font-weight: 600;
  color: var(--ink);
`;

const Verb = styled.span`
  color: var(--ink-muted);
`;

const Target = styled.span.attrs({ className: 'activity-target' })`
  font-weight: 600;
  color: var(--terracotta);
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  color: var(--ink-muted);
  flex-wrap: wrap;
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--ink-faint);
  flex-shrink: 0;
`;

const Pill = styled.span<{ $variant: 'shared' | 'private' | 'email' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 9px;
  border-radius: 999px;
  text-transform: uppercase;

  svg {
    width: 10px;
    height: 10px;
  }

  ${(p) =>
    p.$variant === 'shared' &&
    `background: rgba(74, 107, 58, 0.12); color: #4A6B3A;`}
  ${(p) =>
    p.$variant === 'private' &&
    `background: rgba(43, 61, 84, 0.08); color: #2B3D54;`}
  ${(p) =>
    p.$variant === 'email' &&
    `background: rgba(200, 75, 31, 0.12); color: var(--terracotta);`}
`;

const Recipient = styled.span`
  font-style: normal;
  color: var(--ink);
  font-weight: 500;
`;

const When = styled.span`
  font-size: 11.5px;
  color: var(--ink-faint);
  white-space: nowrap;
  flex-shrink: 0;
  align-self: flex-start;
  padding-top: 8px;
  font-variant-numeric: tabular-nums;
`;

const EmptyState = styled.div`
  padding: 48px 32px;
  text-align: center;
  color: var(--ink-muted);
  font-size: 13px;
`;
